import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { projectService, renderService, activityService } from '../../utils/supabaseQueries';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import LoadingIndicator from '../../components/ui/LoadingIndicator';
import ProjectTable from './components/ProjectTable';
import FilterToolbar from './components/FilterToolbar';
import RenderingQueue from './components/RenderingQueue';
import ProjectStats from './components/ProjectStats';
import ExportHistory from './components/ExportHistory';
import NewProjectModal from './components/NewProjectModal';
import EditProjectModal from './components/EditProjectModal';

const ProjectManagement = () => {
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  
  // State management
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [renderingJobs, setRenderingJobs] = useState([]);
  const [exports, setExports] = useState([]);
  const [stats, setStats] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [dateRange, setDateRange] = useState('all');
  const [sortConfig, setSortConfig] = useState({ key: 'updated_at', direction: 'desc' });
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [showNewProjectModal, setShowNewProjectModal] = useState(false);
  const [showEditProjectModal, setShowEditProjectModal] = useState(false);
  const [editingProject, setEditingProject] = useState(null);

  // Load initial data
  useEffect(() => {
    const loadProjectData = async () => {
      if (!user) return;
      
      setIsLoading(true);
      setError(null);
      
      try {
        // Load user's projects
        const { data: projectsData, error: projectsError } = await projectService?.getUserProjects();
        if (projectsError) throw projectsError;

        // Transform project data to match UI expectations
        const transformedProjects = projectsData?.map(project => ({
          id: project?.id,
          name: project?.name,
          type: project?.metadata?.type || 'film',
          template: project?.metadata?.template || 'clean',
          budget: project?.metadata?.budget || 'under_500k',
          status: project?.status,
          createdAt: project?.created_at,
          modifiedAt: project?.updated_at,
          credits: project?.credits?.length || 0,
          owner: project?.owner,
          collaborators: project?.collaborators || [],
          videoFiles: project?.video_files || [],
          renderJobs: project?.render_jobs || []
        })) || [];

        setProjects(transformedProjects);
        setFilteredProjects(transformedProjects);

        // Load rendering queue
        const { data: renderData, error: renderError } = await renderService?.getRenderQueue();
        if (renderError) throw renderError;

        const transformedRenders = renderData?.map(job => ({
          id: job?.id,
          projectName: job?.project?.name || 'Unknown Project',
          status: job?.status === 'processing' ? 'active' : job?.status,
          progress: job?.progress || 0,
          format: job?.settings?.format || 'MP4',
          resolution: job?.settings?.resolution || '1920x1080',
          startedAt: job?.started_at,
          estimatedTimeRemaining: job?.status === 'processing' ? 300 : 0, // Mock estimate
          duration: job?.video_file?.duration || 0,
          fileSize: job?.video_file?.file_size ? `${Math.round(job?.video_file?.file_size / (1024 * 1024))} MB` : 'Unknown',
          priority: job?.settings?.priority || 'normal',
          errorMessage: job?.error_message
        })) || [];

        setRenderingJobs(transformedRenders);

        // Create mock exports from completed renders
        const completedProjects = transformedProjects?.filter(p => p?.status === 'completed');
        const mockExports = completedProjects?.slice(0, 5)?.map((project, index) => ({
          id: `export-${project?.id}`,
          projectName: project?.name,
          format: 'mp4',
          resolution: '1920x1080',
          quality: 'High',
          fps: 24,
          fileSize: project?.fileSize || 15728640,
          createdAt: project?.modifiedAt,
          expiresAt: new Date(new Date(project?.modifiedAt).getTime() + 7 * 24 * 60 * 60 * 1000)?.toISOString(),
          downloadCount: Math.floor(Math.random() * 5) + 1,
          hasAudio: true,
          hasSubtitles: false
        })) || [];

        setExports(mockExports);

        // Calculate stats
        const totalProjects = transformedProjects?.length || 0;
        const projectLimit = 25; // Mock limit
        const storageUsed = transformedProjects?.reduce((sum, p) => sum + p?.fileSize, 0) || 0;
        const storageLimit = 1073741824; // 1GB mock limit
        const rendersThisMonth = transformedProjects?.filter(p => {
          const projectDate = new Date(p?.createdAt);
          const now = new Date();
          return projectDate?.getMonth() === now?.getMonth() && projectDate?.getFullYear() === now?.getFullYear();
        })?.length || 0;
        const renderLimit = 50; // Mock limit
        const activeRenders = transformedRenders?.filter(r => r?.status === 'active')?.length || 0;
        const maxConcurrentRenders = 3; // Mock limit

        setStats({
          totalProjects,
          projectLimit,
          storageUsed,
          storageLimit,
          rendersThisMonth,
          renderLimit,
          activeRenders,
          maxConcurrentRenders
        });

      } catch (error) {
        console.error('Error loading project data:', error);
        setError('Failed to load project data. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    loadProjectData();
  }, [user]);

  // Filter and search logic
  useEffect(() => {
    let filtered = [...projects];

    // Search filter
    if (searchQuery) {
      filtered = filtered?.filter(project =>
        project?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        project?.type?.toLowerCase()?.includes(searchQuery?.toLowerCase())
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered?.filter(project => project?.status === statusFilter);
    }

    // Type filter
    if (typeFilter !== 'all') {
      const typeMap = {
        'title-cards': 'Title Cards',
        'end-crawl': 'End Crawl',
        'combined': 'Combined'
      };
      filtered = filtered?.filter(project => project?.type === typeMap?.[typeFilter]);
    }

    // Date range filter
    if (dateRange !== 'all') {
      const now = new Date();
      const filterDate = new Date();
      
      switch (dateRange) {
        case 'today':
          filterDate?.setHours(0, 0, 0, 0);
          break;
        case 'week':
          filterDate?.setDate(now?.getDate() - 7);
          break;
        case 'month':
          filterDate?.setMonth(now?.getMonth() - 1);
          break;
        case 'quarter':
          filterDate?.setMonth(now?.getMonth() - 3);
          break;
        case 'year':
          filterDate?.setFullYear(now?.getFullYear() - 1);
          break;
      }
      
      if (dateRange !== 'all') {
        filtered = filtered?.filter(project => 
          new Date(project.modifiedAt) >= filterDate
        );
      }
    }

    // Sort
    filtered?.sort((a, b) => {
      let aValue = a?.[sortConfig?.key];
      let bValue = b?.[sortConfig?.key];

      if (sortConfig?.key === 'createdAt' || sortConfig?.key === 'modifiedAt' || sortConfig?.key === 'updated_at') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }

      if (aValue < bValue) {
        return sortConfig?.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig?.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });

    setFilteredProjects(filtered);
  }, [projects, searchQuery, statusFilter, typeFilter, dateRange, sortConfig]);

  // Event handlers
  const handleSelectProject = (projectId, checked) => {
    if (checked) {
      setSelectedProjects([...selectedProjects, projectId]);
    } else {
      setSelectedProjects(selectedProjects?.filter(id => id !== projectId));
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedProjects(filteredProjects?.map(p => p?.id));
    } else {
      setSelectedProjects([]);
    }
  };

  const handleSort = (config) => {
    setSortConfig(config);
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setStatusFilter('all');
    setTypeFilter('all');
    setDateRange('all');
  };

  const handleEditProject = (projectId) => {
    const project = projects?.find(p => p?.id === projectId);
    if (project) {
      setEditingProject(project);
      setShowEditProjectModal(true);
    }
  };

  const handleDuplicateProject = async (projectId) => {
    try {
      const project = projects?.find(p => p?.id === projectId);
      if (!project) return;

      const { data, error } = await projectService?.createProject({
        name: `${project?.name} (Copy)`,
        description: project?.description,
        status: 'draft',
        metadata: { ...project?.metadata, type: project?.type }
      });

      if (error) throw error;

      // Log activity
      await activityService?.logActivity(data?.id, 'project_created', {
        originalProjectId: projectId,
        duplicated: true
      });

      // Refresh projects list
      const { data: updatedProjects } = await projectService?.getUserProjects();
      const transformedProjects = updatedProjects?.map(project => ({
        id: project?.id,
        name: project?.name,
        type: project?.metadata?.type || 'Combined',
        status: project?.status,
        createdAt: project?.created_at,
        modifiedAt: project?.updated_at,
        fileSize: project?.video_files?.reduce((sum, file) => sum + (file?.file_size || 0), 0) || 0,
        credits: project?.credits?.length || 0
      })) || [];
      
      setProjects(transformedProjects);
    } catch (error) {
      console.error('Error duplicating project:', error);
      setError('Failed to duplicate project. Please try again.');
    }
  };

  const handleExportProject = (projectId) => {
    navigate('/video-rendering', { state: { projectId } });
  };

  const handleDeleteProject = async (projectId) => {
    try {
      const { error } = await projectService?.deleteProject(projectId);
      if (error) throw error;

      setProjects(projects?.filter(p => p?.id !== projectId));
      setSelectedProjects(selectedProjects?.filter(id => id !== projectId));
    } catch (error) {
      console.error('Error deleting project:', error);
      setError('Failed to delete project. Please try again.');
    }
  };

  const handleBulkDelete = () => {
    setShowBulkDeleteConfirm(true);
  };

  const confirmBulkDelete = async () => {
    try {
      // Delete selected projects
      await Promise.all(
        selectedProjects?.map(projectId => projectService?.deleteProject(projectId))
      );

      setProjects(projects?.filter(p => !selectedProjects?.includes(p?.id)));
      setSelectedProjects([]);
      setShowBulkDeleteConfirm(false);
    } catch (error) {
      console.error('Error deleting projects:', error);
      setError('Failed to delete projects. Please try again.');
    }
  };

  const handleBulkExport = () => {
    navigate('/video-rendering', { state: { projectIds: selectedProjects } });
  };

  const handleCancelJob = async (jobId) => {
    try {
      const { error } = await renderService?.updateRenderJob(jobId, { 
        status: 'cancelled' 
      });
      if (error) throw error;

      setRenderingJobs(renderingJobs?.map(job => 
        job?.id === jobId ? { ...job, status: 'cancelled' } : job
      ));
    } catch (error) {
      console.error('Error cancelling job:', error);
      setError('Failed to cancel render job. Please try again.');
    }
  };

  const handleRetryJob = async (jobId) => {
    try {
      const { error } = await renderService?.updateRenderJob(jobId, { 
        status: 'pending',
        error_message: null
      });
      if (error) throw error;

      setRenderingJobs(renderingJobs?.map(job => 
        job?.id === jobId ? { ...job, status: 'pending', errorMessage: null } : job
      ));
    } catch (error) {
      console.error('Error retrying job:', error);
      setError('Failed to retry render job. Please try again.');
    }
  };

  const handleRedownload = (exportId) => {
    console.log('Redownloading export:', exportId);
    // Implementation would depend on file storage setup
  };

  const handleDeleteExport = (exportId) => {
    setExports(exports?.filter(exp => exp?.id !== exportId));
  };

  const handleNewProject = () => {
    setShowNewProjectModal(true);
  };

  const handleProjectCreated = (newProject) => {
    // Navigate to credit creation page with the new project
    navigate('/credit-creation', { state: { projectId: newProject?.id } });
  };

  const handleProjectUpdated = (updatedProject) => {
    // Transform the updated project to match UI expectations
    const transformedProject = {
      id: updatedProject?.id,
      name: updatedProject?.name,
      type: updatedProject?.metadata?.type || 'film',
      template: updatedProject?.metadata?.template || 'clean',
      budget: updatedProject?.metadata?.budget || 'under_500k',
      status: updatedProject?.status,
      createdAt: updatedProject?.created_at,
      modifiedAt: updatedProject?.updated_at,
      credits: 0, // Will be updated when credits are loaded
      owner: updatedProject?.owner,
      collaborators: updatedProject?.collaborators || [],
      videoFiles: updatedProject?.video_files || [],
      renderJobs: updatedProject?.render_jobs || []
    };

    // Update projects list
    setProjects(prevProjects => 
      prevProjects?.map(project => 
        project?.id === updatedProject?.id ? transformedProject : project
      )
    );

    setShowEditProjectModal(false);
    setEditingProject(null);
  };

  const handleNewProjectFromEmpty = () => {
    setShowNewProjectModal(true);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header 
          userRole={userProfile?.role || user?.user_metadata?.role || 'user'} 
          userName={userProfile?.full_name || user?.user_metadata?.full_name || user?.email}
          userAvatar={userProfile?.avatar_url}
        />
        <main className="pt-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <LoadingIndicator message="Loading project management..." />
          </div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <Header 
          userRole={userProfile?.role || user?.user_metadata?.role || 'user'} 
          userName={userProfile?.full_name || user?.user_metadata?.full_name || user?.email}
          userAvatar={userProfile?.avatar_url}
        />
        <main className="pt-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
              <Icon name="AlertTriangle" size={48} className="text-red-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-red-800 mb-2">Error Loading Projects</h3>
              <p className="text-red-600 mb-4">{error}</p>
              <Button onClick={() => window.location?.reload()}>
                Try Again
              </Button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header 
        userRole={userProfile?.role || user?.user_metadata?.role || 'user'} 
        userName={userProfile?.full_name || user?.user_metadata?.full_name || user?.email}
        userAvatar={userProfile?.avatar_url}
      />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Breadcrumb />
          
          {/* Page Header */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">Project Management</h1>
              <p className="text-muted-foreground">
                Manage your film credit projects, monitor rendering progress, and access export history.
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3 mt-4 lg:mt-0">
              <Button
                variant="outline"
                iconName="Upload"
                onClick={() => navigate('/credit-creation')}
              >
                Import Excel
              </Button>
              <Button
                iconName="Plus"
                onClick={handleNewProject}
              >
                New Project
              </Button>
            </div>
          </div>

          {/* Project Statistics */}
          <ProjectStats stats={stats} />

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
            {/* Projects Section */}
            <div className="xl:col-span-2 space-y-6">
              {/* Filter Toolbar */}
              <FilterToolbar
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
                statusFilter={statusFilter}
                onStatusFilterChange={setStatusFilter}
                typeFilter={typeFilter}
                onTypeFilterChange={setTypeFilter}
                dateRange={dateRange}
                onDateRangeChange={setDateRange}
                onClearFilters={handleClearFilters}
                selectedCount={selectedProjects?.length}
                onBulkDelete={handleBulkDelete}
                onBulkExport={handleBulkExport}
              />

              {/* Projects Table */}
              <ProjectTable
                projects={filteredProjects}
                selectedProjects={selectedProjects}
                onSelectProject={handleSelectProject}
                onSelectAll={handleSelectAll}
                onEditProject={handleEditProject}
                onDuplicateProject={handleDuplicateProject}
                onExportProject={handleExportProject}
                onDeleteProject={handleDeleteProject}
                sortConfig={sortConfig}
                onSort={handleSort}
              />

              {/* Empty State */}
              {filteredProjects?.length === 0 && (
                <div className="bg-card border border-border rounded-lg p-12 text-center">
                  <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon name="FolderOpen" size={32} className="text-muted-foreground" />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">No projects found</h3>
                  <p className="text-muted-foreground mb-6">
                    {searchQuery || statusFilter !== 'all' || typeFilter !== 'all' || dateRange !== 'all' ? 'Try adjusting your filters or search terms.': 'Get started by creating your first film credit project.'}
                  </p>
                  <Button
                    iconName="Plus"
                    onClick={handleNewProjectFromEmpty}
                  >
                    Create New Project
                  </Button>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Rendering Queue */}
              <RenderingQueue
                renderingJobs={renderingJobs}
                onCancelJob={handleCancelJob}
                onRetryJob={handleRetryJob}
              />

              {/* Export History */}
              <ExportHistory
                exports={exports}
                onRedownload={handleRedownload}
                onDeleteExport={handleDeleteExport}
              />
            </div>
          </div>
        </div>
      </main>

      {/* Bulk Delete Confirmation Modal */}
      {showBulkDeleteConfirm && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-lg p-6 max-w-md w-full shadow-elevation-3">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <Icon name="AlertTriangle" size={20} className="text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground">Delete Projects</h3>
                <p className="text-sm text-muted-foreground">This action cannot be undone.</p>
              </div>
            </div>
            
            <p className="text-sm text-muted-foreground mb-6">
              Are you sure you want to delete {selectedProjects?.length} selected project{selectedProjects?.length !== 1 ? 's' : ''}? 
              All associated data will be permanently removed.
            </p>
            
            <div className="flex space-x-3">
              <Button
                variant="outline"
                onClick={() => setShowBulkDeleteConfirm(false)}
                fullWidth
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={confirmBulkDelete}
                fullWidth
              >
                Delete {selectedProjects?.length} Project{selectedProjects?.length !== 1 ? 's' : ''}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Project Modal */}
      <EditProjectModal
        isOpen={showEditProjectModal}
        onClose={() => {
          setShowEditProjectModal(false);
          setEditingProject(null);
        }}
        project={editingProject}
        onProjectUpdated={handleProjectUpdated}
      />

      {/* New Project Modal */}
      <NewProjectModal
        isOpen={showNewProjectModal}
        onClose={() => setShowNewProjectModal(false)}
        onProjectCreated={handleProjectCreated}
      />
    </div>
  );
};

export default ProjectManagement;